import { useState } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  Skeleton,
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import Label from '../../components/Label';
// hooks
import useResponsive from '../../hooks/useResponsive';
// swr
import useSWR from 'swr';
// utils
import axios from '../../utils/axios';
import axiosSWRFetcher from '../../utils/swrFetcher';
import { fKrTrackingTimeKST } from '../../utils/formatTime';

export default function DailyDoneTodoDialog({ isOpen, closeDialog, row, companyId, reloadData }) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const smUp = useResponsive('up', 'sm');
  const router = useRouter();
  const { c } = router.query;

  const isRunning = !row?.end_at;
  const modified = row?.modified_start_at && row?.modified_end_at;
  const startDateTime = modified ? row?.modified_start_at : row?.start_at;
  const endDateTime = isRunning ? new Date() : modified ? row?.modified_end_at : row?.end_at;
  const duration = new Date(endDateTime) - new Date(startDateTime);
  const hours = Math.floor(duration / (1000 * 60 * 60));
  const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));

  const timeTrackingItemId = row && row.id;
  const { data, isLoading } = useSWR((row && isOpen && timeTrackingItemId) ? `/api/works/todo/crew/company/${c}/share/${timeTrackingItemId}/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});

  const openDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  }

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  }

  const openTimeTrackingItemFile = async (item) => {
    const response = await axios.get(`/api/works/todo/crew/company/${c}/share/${timeTrackingItemId}/file/${item.id}/`);
    window.open(response.data.url, '_blank', 'noopener, noreferrer');
  };

  return (
    <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogContent sx={{ my: { xs: 0, sm: 1}  }}>
        {data?.is_manual && (
          <Label variant="outlined" color="primary" sx={{ mb: .5 }}>
            수동 등록함
          </Label>
        )}
        <Typography variant={smUp ? "h5" : "h6"} gutterBottom>
          근무 시간과 진행 업무
        </Typography>
        <Typography sx={{ color: 'text.secondary' }} variant={smUp ? "body1" : "body2"}>
          출퇴근 시간과 기록한 내용이 보여집니다.
        </Typography>
        <Box
          sx={{
            p: 3,
            my: 2,
            backgroundColor: "grey.200",
            borderRadius: 2,
          }}
        >
          {!isRunning && (
          <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary", mb: 0.5 }}>
            총 근무 시간
          </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: 20, height: 20, color: "text.secondary" }} />
            <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
              {hours > 0 ? `${hours}시간` : ""} {minutes}분 {isRunning && " 근무중"}
            </Typography>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} sx={{ mt: 3 }} spacing={smUp ? 2 : 1.5}>
            <Stack spacing={.5}
              sx={{ flexGrow: 1 }}
            >
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                출근 시간
              </Typography>
              <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                {fKrTrackingTimeKST(startDateTime)}
              </Typography>
            </Stack>
            {smUp && (
            <Stack spacing={.5}>
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary", visibility: "hidden" }}>
                -
              </Typography>
              <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                ~
              </Typography>
            </Stack>
            )}
            <Stack spacing={.5}
              sx={{ flexGrow: 1 }}
            >
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                퇴근 시간
              </Typography>
              <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                {isRunning ? "-" : fKrTrackingTimeKST(endDateTime)}
              </Typography>
            </Stack>
          </Stack>
        </Box>
        {isLoading && (
          <Box
            sx={{
              p: 3,
              mt: 2,
              backgroundColor: 'grey.200',
              borderRadius: 2,
            }}
          >
            <Skeleton variant="rectangular" height={50} />
          </Box>
        )}
        {!isLoading && data && (data.select.length > 0 || data.memo_at_start) && (
        <Box
          sx={{
            p: 3,
            mt: 2,
            mb: isRunning ? 0 : 2,
            backgroundColor: 'grey.200',
            borderRadius: 2,
          }}
        >
          {data.select.length > 0 && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "text.secondary" }}>
              진행 예정 투두
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {!isLoading && data && data.select.map((done, index) => (
                <Typography sx={{ color: "text.secondary" }} key={index} variant={smUp ? "body1" : "body2"}>
                  • {done.content}
                </Typography>
              ))}
            </Stack>
            </>
          )}
          {data.memo_at_start && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ mt: data?.select?.length > 0 ? 2 : 0, mb: 1, color: "text.secondary" }}>
              출근 메모
            </Typography>
            <Typography sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }} variant={smUp ? "body1" : "body2"}>
              {data.memo_at_start}
            </Typography>
            </>
          )}
        </Box>
        )}
        {!isRunning && (
        <Box
          sx={{
            p: 3,
            mt: 2,
            backgroundColor: 'grey.200',
            borderRadius: 2,
          }}
        >
          {isLoading && (
            <Skeleton variant="rectangular" height={25} />
          )}
          {!isLoading && data && data.share.length === 0 && !data.memo && !data.next_work_schedule && (
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "text.secondary" }}>
              완료한 투두와 메모가 없습니다.
            </Typography>
          )}
          {!isLoading && data && data.share.length > 0 && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "text.secondary" }}>
              완료한 투두
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {!isLoading && data && data.share.map((done, index) => (
                <Typography sx={{ color: "text.secondary" }} key={index} variant={smUp ? "body1" : "body2"}>
                  • {done.content}
                </Typography>
              ))}
            </Stack>
            </>
          )}
          {!isLoading && data && data.memo && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ mt: data?.share?.length > 0 ? 2 : 0, mb: 1, color: "text.secondary" }}>
              메모
            </Typography>
            <Typography sx={{ color: "text.secondary", whiteSpace: "pre-wrap", wordBreak: "break-all" }} variant={smUp ? "body1" : "body2"}>
              {data.memo}
            </Typography>
            {data?.files?.length > 0 && data.files.map((file, index) => (
              <Typography
                key={index} variant={smUp ? "subtitle1" : "subtitle2"}
                sx={{
                  color: "text.secondary", textDecoration: "underline",
                  ":hover": { cursor: "pointer" },
                }}
                onClick={() => openTimeTrackingItemFile(file)}
              >
                {file.name}
              </Typography>
            ))}
            </>
          )}
          {!isLoading && data && data.next_work_schedule && (
            <>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ mt: data?.share?.length > 0 || data?.memo ? 2 : 0, mb: 1, color: "text.secondary" }}>
              다음 출근 계획
            </Typography>
            <Typography sx={{ color: "text.secondary", whiteSpace: "pre-wrap", wordBreak: "break-all" }} variant={smUp ? "body1" : "body2"}>
              {data.next_work_schedule}
            </Typography>
            </>
          )}
        </Box>
        )}

      </DialogContent>
      <DialogActions>
        {!isRunning && (
        <Button
          sx={{ px: 4 }}
          fullWidth={smUp ? false : true}
          onClick={openDeleteDialog}
          variant="text"
          color="error"
          size="large"
        >
          삭제
        </Button>
        )}
        <Button
          sx={{ px: 6 }}
          fullWidth={smUp ? false : true}
          onClick={closeDialog}
          variant="contained"
          size="large"
        >
          확인
        </Button>
      </DialogActions>
      <DeleteDialog
        isOpen={isDeleteDialogOpen}
        closeDialog={closeDeleteDialog}
        closeAll={closeDialog}
        row={row}
        companyId={companyId}
        reloadData={reloadData}
      />
    </Dialog>
  )
}

function DeleteDialog({ isOpen, closeDialog, closeAll, row, companyId, reloadData }) {
  const smUp = useResponsive('up', 'sm');
  const [isSubmit, setIsSubmit] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const deleteItem = async () => {
    try {
      setIsSubmit(true);
      await axios.delete(`/api/works/time-tracking/crew/company/${companyId}/${row?.id}/`);
      closeAll();
      closeDialog();
      reloadData();
      enqueueSnackbar(`출퇴근 기록이 삭제되었습니다.`);
      setIsSubmit(false);
    } catch (err) {
      setIsSubmit(false);
      console.error(err);
    }
  }

  return (
    <Dialog open={isOpen} fullWidth maxWidth="xs">
      <DialogContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 20, height: 20, color: 'error.main' }} />
          <Typography variant="subtitle2" sx={{ color: "error.main" }}>
            잠깐, 꼭 확인해주세요
          </Typography>
        </Stack>
        <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
          출근 기록을 삭제하면 복구되지 않습니다.
        </Typography>
        <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
          그래도 삭제하시겠어요?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} sx={{ width: '100%' }} justifyContent="flex-end">
          <LoadingButton
            loading={isSubmit}
            onClick={closeDialog}
            size="large"
            sx={{ px: 6 }}
            fullWidth={!smUp}
          >
            취소
          </LoadingButton>
          <LoadingButton
            loading={isSubmit}
            variant="contained"
            onClick={deleteItem}
            size="large"
            sx={{ px: 6 }}
            fullWidth={!smUp}
          >
            삭제하기
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
